import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/Common";
import { ShieldAlert, Search, CheckCircle2, XCircle, X, Download, FileText } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLORS = (a) => {
  if (a.includes("login") || a.includes("verify_pin")) return "bg-sky-50 text-sky-700 border-sky-200";
  if (a.includes("sensitive")) return "bg-rose-50 text-rose-700 border-rose-200";
  if (a.includes("approve") || a.includes("update")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const fmtTs = (ts) => ts ? new Date(ts).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (action) params.set("action", action);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    return params;
  }, [q, action, start, end]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit-log?${buildParams().toString()}`);
      setRows(data.rows || []);
      setActions(data.actions || []);
    } finally { setLoading(false); }
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);

  const clear = () => { setQ(""); setAction(""); setStart(""); setEnd(""); };
  const hasFilters = q || action || start || end;

  const download = async (format) => {
    try {
      const url = `/audit-log/export${format === "pdf" ? ".pdf" : ""}?${buildParams().toString()}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `vera-audit-log.${format === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Audit log exported as ${format.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div>
      <PageHeader testid="audit-header" title="Audit Log"
        subtitle="Every sensitive action logged — who, what, when, from where. HIPAA §164.312(b) audit controls.">
        <Button data-testid="audit-export-csv" variant="outline" onClick={() => download("csv")} className="gap-1.5 h-9">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
        <Button data-testid="audit-export-pdf" onClick={() => download("pdf")} className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
          <FileText className="w-4 h-4" /> Export PDF
        </Button>
      </PageHeader>

      <Card className="p-4 border-slate-200 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Search user / IP / resource / detail</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="audit-search" placeholder="email, IP, keyword…" value={q}
                onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm mt-1">
              <option value="">All actions</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">From date</Label>
            <Input data-testid="audit-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">To date</Label>
            <Input data-testid="audit-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-9" />
          </div>
        </div>
        {hasFilters && (
          <Button data-testid="audit-clear" variant="ghost" size="sm" onClick={clear} className="mt-3 h-8 gap-1 text-slate-500">
            <X className="w-3.5 h-3.5" /> Clear filters
          </Button>
        )}
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        {rows.length === 0 ? (
          <EmptyState icon={ShieldAlert} title={hasFilters ? "No entries match your filters" : "No audit entries yet"} />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50/60" data-testid={`audit-row-${r.id}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{fmtTs(r.ts)}</TableCell>
                    <TableCell className="text-sm">{r.actor_email || <span className="text-slate-300">anonymous</span>}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${ACTION_COLORS(r.action)}`}>{r.action}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{r.resource}</div>
                      {r.resource_id && <div className="text-xs text-slate-400 font-mono truncate max-w-[160px]">{r.resource_id}</div>}
                    </TableCell>
                    <TableCell>
                      {r.success
                        ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> ok</span>
                        : <span className="inline-flex items-center gap-1 text-rose-700 text-xs"><XCircle className="w-3.5 h-3.5" /> fail</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.ip || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[280px] truncate" title={r.detail}>{r.detail || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      {loading && <div className="text-xs text-slate-400 mt-2">Loading…</div>}
      <p className="text-xs text-slate-400 mt-3">
        Showing up to 200 most-recent entries. Exports respect current filters (up to 5,000 rows). Audit records are retained indefinitely and cannot be edited from the UI — read-only for compliance.
      </p>
    </div>
  );
}
